import {
  adminPartnerSchema,
  mediaVariant,
  PARTNER_GROUP_LABELS,
  PARTNER_GROUPS,
  partnerGroupSchema,
  type AdminPartner,
  type PartnerGroup,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { ImageField, useImagesBusy } from '../media/ImageUpload';

const BASE = '/api/site/content/parceiros';

const sameGroup = (a: AdminPartner | undefined, b: AdminPartner | undefined) =>
  a !== undefined && b !== undefined && a.group === b.group;

/**
 * Parceiros e patrocinadores (decidido pelo GrowuP a 19-10-2026): uma so
 * lista para a pagina growup.gg/parceiros e o carrossel junto ao rodape.
 * Os 14 da pagina antiga comecam em rascunho, sem logotipo. Ver docs/conteudos.
 */
export function PartnersView() {
  const [list, setList] = useState<AdminPartner[] | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  // Muda so ao abrir o editor: criar um parceiro nao o volta a montar.
  const [opened, setOpened] = useState(0);
  const open = (id: string | 'new') => {
    setOpened((n) => n + 1);
    setEditing(id);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, z.array(adminPartnerSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const swap = async (i: number, j: number) => {
    if (!list) return;
    const ids = list.map((p) => p.id);
    const a = ids[i];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[i] = b;
    ids[j] = a;
    setBusy(true);
    setError(null);
    try {
      setList(z.array(adminPartnerSchema).parse(await send('PUT', `${BASE}/order`, { ids })));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing !== null) {
    const current = list?.find((p) => p.id === editing) ?? null;
    return (
      <PartnerEditor
        key={opened}
        partner={current}
        onSaved={(saved) => {
          setList((l) => {
            if (!l) return [saved];
            return l.some((p) => p.id === saved.id)
              ? l.map((p) => (p.id === saved.id ? saved : p))
              : [...l, saved];
          });
          setEditing(saved.id);
        }}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const published = list?.filter((p) => p.status === 'published').length ?? 0;
  let lastGroup: PartnerGroup | null = null;

  return (
    <Card
      headingLevel={2}
      title="Parceiros"
      actions={
        <Button size="sm" onClick={() => open('new')}>
          Novo parceiro
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Os parceiros e patrocinadores da Grow uP, para a página de parceiros e o carrossel junto
          ao rodapé. Publicar pede logótipo. As setas mudam a ordem dentro do mesmo grupo.{' '}
          {list ? `${String(published)} publicados.` : null}
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {list === null && !error ? <p>A carregar…</p> : null}
        {list && list.length === 0 ? <p>Ainda não há parceiros.</p> : null}
        <ol className="flex flex-col gap-3">
          {list?.map((p, i) => {
            const heading = p.group !== lastGroup ? PARTNER_GROUP_LABELS[p.group] : null;
            lastGroup = p.group;
            return (
              <li key={p.id} className="flex flex-col gap-2">
                {heading !== null ? (
                  <h3 className="mt-2 font-display text-title text-gu-ink">{heading}</h3>
                ) : null}
                <div className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2">
                  {p.logoUrl ? (
                    <img
                      src={mediaVariant(p.logoUrl, 'sm')}
                      alt=""
                      className="h-12 w-24 bg-gu-sunken object-contain p-1"
                    />
                  ) : (
                    <div className="flex h-12 w-24 items-center justify-center border border-dashed border-gu-line text-caption text-gu-ink-muted">
                      Sem logótipo
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="font-semibold">{p.name}</span>
                    <span className="text-caption text-gu-ink-muted">
                      {p.sinceYear ? `Desde ${String(p.sinceYear)} · ` : ''}
                      {p.inCarousel ? 'No carrossel' : 'Fora do carrossel'}
                      {p.endsOn ? ` · até ${p.endsOn.split('-').reverse().join('/')}` : ''}
                    </span>
                    <span>
                      <StatusBadge tone={p.status === 'published' ? 'success' : 'neutral'}>
                        {p.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </StatusBadge>
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy || !sameGroup(list[i - 1], p)}
                      aria-label={`Subir ${p.name}`}
                      onClick={() => void swap(i, i - 1)}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy || !sameGroup(p, list[i + 1])}
                      aria-label={`Descer ${p.name}`}
                      onClick={() => void swap(i, i + 1)}
                    >
                      ↓
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => open(p.id)}>
                      Editar
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Card>
  );
}

function PartnerEditor({
  partner,
  onSaved,
  onClose,
}: {
  partner: AdminPartner | null;
  onSaved: (p: AdminPartner) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(partner?.name ?? '');
  const [group, setGroup] = useState<PartnerGroup>(partner?.group ?? 'partner');
  const [logoUrl, setLogoUrl] = useState<string | null>(partner?.logoUrl ?? null);
  const [websiteUrl, setWebsiteUrl] = useState(partner?.websiteUrl ?? '');
  const [description, setDescription] = useState(partner?.description ?? '');
  const [sinceYear, setSinceYear] = useState(partner?.sinceYear?.toString() ?? '');
  const [inCarousel, setInCarousel] = useState(partner?.inCarousel ?? true);
  const [startsOn, setStartsOn] = useState(partner?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(partner?.endsOn ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const images = useImagesBusy();

  const isPublished = partner?.status === 'published';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      group,
      logoUrl,
      websiteUrl,
      description,
      sinceYear: sinceYear === '' ? null : Number(sinceYear),
      inCarousel,
      startsOn: startsOn === '' ? null : startsOn,
      endsOn: endsOn === '' ? null : endsOn,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const res = partner
        ? await send('PUT', `${BASE}/${partner.id}`, payload)
        : await send('POST', BASE, payload);
      onSaved(adminPartnerSchema.parse(res));
      setSaved(partner ? 'Guardado.' : 'Criado como rascunho.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const run = async (path: string) => {
    if (!partner) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminPartnerSchema.parse(await send('POST', `${BASE}/${partner.id}${path}`)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!partner) return;
    if (!window.confirm(`Apagar o parceiro «${partner.name}»? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', `${BASE}/${partner.id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Parceiros
        </Button>
      </div>
      <Card headingLevel={2} title={partner ? partner.name : 'Novo parceiro'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <div className="grid items-start gap-4 md:grid-cols-[14rem_1fr]">
            <ImageField
              label="Logótipo"
              optional
              value={logoUrl}
              onChange={setLogoUrl}
              onBusyChange={images.track('logotipo')}
              error={fields.logoUrl}
              hint="De preferência com fundo transparente (PNG). Obrigatório para publicar."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Nome" error={fields.name} className="sm:col-span-2">
                <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Grupo" error={fields.group}>
                <Select
                  value={group}
                  onChange={(e) => setGroup(partnerGroupSchema.parse(e.target.value))}
                >
                  {PARTNER_GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {PARTNER_GROUP_LABELS[g]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Parceiro desde" optional error={fields.sinceYear}>
                <Input
                  inputMode="numeric"
                  placeholder="aaaa"
                  maxLength={4}
                  value={sinceYear}
                  onChange={(e) => setSinceYear(e.target.value.replace(/\D/g, ''))}
                />
              </Field>
              <Field
                label="Site do parceiro"
                optional
                hint="https://…"
                error={fields.websiteUrl}
                className="sm:col-span-2"
              >
                <Input
                  value={websiteUrl}
                  maxLength={500}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                />
              </Field>
            </div>
          </div>
          <Field
            label="Descrição curta"
            optional
            hint="Duas a quatro linhas sobre o parceiro e a parceria."
            error={fields.description}
          >
            <Textarea
              rows={3}
              value={description}
              maxLength={400}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Início"
              optional
              hint="Antes deste dia não aparece no site."
              error={fields.startsOn}
            >
              <DateInput value={startsOn} onChange={setStartsOn} />
            </Field>
            <Field
              label="Fim"
              optional
              hint="Depois deste dia sai sozinho do site."
              error={fields.endsOn}
            >
              <DateInput value={endsOn} onChange={setEndsOn} />
            </Field>
          </div>
          <Checkbox
            label="Mostrar no carrossel junto ao rodapé"
            checked={inCarousel}
            onChange={(e) => setInCarousel(e.target.checked)}
          />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy || images.busy}>
              {partner ? 'Guardar' : 'Criar rascunho'}
            </Button>
            {partner && !isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy || partner.logoUrl === null}
                title={
                  partner.logoUrl === null ? 'Carregue e guarde o logótipo primeiro' : undefined
                }
                onClick={() => void run('/publish')}
              >
                Publicar
              </Button>
            ) : null}
            {isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/unpublish')}
              >
                Tirar do site
              </Button>
            ) : null}
            {partner && !isPublished ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            ) : null}
          </div>
          {partner ? (
            <p className="text-caption text-gu-ink-muted">
              {isPublished ? 'Publicado. ' : 'Rascunho: ainda não aparece no site. '}
              Última alteração: {formatDateTime(partner.updatedAt)} por {partner.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
